import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { catchError, map, Observable, switchMap, take, throwError } from 'rxjs';
import { ConfigService } from 'src/app/core/services/config.service';
import { AuthLogoutSuccessAction } from 'src/app/core/store/actions/auth';
import { PortalUpdateJWTAction } from 'src/app/core/store/actions/portal';
import { getJWTSelector } from 'src/app/core/store/reducers/portal';
import { parseServerError } from 'src/app/core/utils/app-utils';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly refreshTokenUrl: string;
  private readonly jwt$ = this.store$.pipe(select(getJWTSelector));

  constructor(
    protected readonly store$: Store,
    protected readonly http: HttpClient,
    protected readonly config: ConfigService,
  ) {
    this.refreshTokenUrl = `${config.env.apiRoot}/iportal/token`;
  }

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(catchError((res) => this.catchErrors(res, req)));
  }

  private catchErrors(res: HttpErrorResponse, req: HttpRequest<any>): Observable<any> {
    const hasJWTHeader = req.headers.has(this.config.env.jwtTokenHeader);
    const isRepeatReq = req.url === this.refreshTokenUrl || req.params.has('repeat');

    if (res.status === 401) {
      if (hasJWTHeader && !isRepeatReq) {
        return this.refreshJWT().pipe(
          switchMap(() => this.repeatRequest(req)),
          catchError(() => {
            this.redirectToLogin();
            return throwError(() => res);
          }),
        );
      } else {
        this.redirectToLogin();
      }
    }

    const error = parseServerError(res);
    return throwError(() => error);
  }

  private repeatRequest(req: HttpRequest<any>) {
    return this.jwt$.pipe(
      take(1),
      switchMap((jwt) => {
        const params = req.params.set('repeat', true);
        const headers = req.headers.set(this.config.env.jwtTokenHeader, jwt || '');
        return this.http.request(req.clone({ headers, params }));
      }),
    );
  }

  private refreshJWT() {
    return this.http
      .get(this.refreshTokenUrl, {
        headers: {
          [this.config.env.domainHeader]: this.config.hostname,
        },
      })
      .pipe(
        map((res) => {
          const token: string = (res as any).data;
          this.store$.dispatch(new PortalUpdateJWTAction(token));
        }),
      );
  }

  private redirectToLogin() {
    this.store$.dispatch(new AuthLogoutSuccessAction());
  }
}
